import { accountMatchesFilters } from "@common/helpers/domains/accounting/accountMatchesFilters";
import {
    accountMatchesCoaGroupKeys,
    buildCoaGroupFilterOptions,
    buildCoaGroupFilterOptionsFromChartAccounts,
    CoaGroupFilterOption,
} from "@common/helpers/domains/accounting/coaGroupFilter";
import { AccountRole } from "@common/types/domains/accounting/account";
import { FiltersTypes } from "@common/types/filters/general";
import { DraftAccount, DraftGroup } from "./draftOperations";
import { GetRowCapabilities } from "./editorCapabilities";
import { ChartOfAccountsTemplateEditorRow } from "./editorRowTypes";
import { draftGroupsToAccountGroups } from "./nested/draftGroupsToAccountGroups";

// A workspace host passes `displayedEntityIds` and shows one row per merged
// group, so its option keys must list every constituent id the way the live
// chart's keys do. Both surfaces share `filters.coaTemplateGroupKeys`, and the
// panel matches keys exactly, so a single-id key here would read as unselected
// there. Templates omit `displayedEntityIds` and stay 1:1.
export const buildTemplateEditorGroupOptions = ({
    rows,
    groups,
    displayedEntityIds,
}: {
    rows: ChartOfAccountsTemplateEditorRow[];
    groups: DraftGroup[];
    displayedEntityIds?: number[];
}): CoaGroupFilterOption[] =>
    displayedEntityIds === undefined
        ? buildCoaGroupFilterOptions({
              accounts: rows.map((row) => ({
                  type: row.type,
                  customGroupChainIds: row.customGroupChainIds,
              })),
              groups,
          })
        : buildCoaGroupFilterOptionsFromChartAccounts({
              accounts: rows.map((row) => ({
                  type: row.type,
                  entityId: row.account.entityId,
                  parentGroupId: row.account.parentGroupId,
              })),
              groups: draftGroupsToAccountGroups(groups),
              displayedEntityIds,
          });

export const filterTemplateEditorRows = ({
    rows,
    filters,
    searchFilter,
    getAccountRole,
    accountIdsWithEnabledRules,
    getRowCapabilities,
}: {
    rows: ChartOfAccountsTemplateEditorRow[];
    filters: FiltersTypes;
    searchFilter?: string;
    getAccountRole?: (account: DraftAccount) => AccountRole | null;
    accountIdsWithEnabledRules?: ReadonlySet<string>;
    getRowCapabilities?: GetRowCapabilities;
}): ChartOfAccountsTemplateEditorRow[] => {
    const isSearching = Boolean(searchFilter?.trim());
    return rows.filter((row) => {
        const placeholderLabel = isSearching
            ? getRowCapabilities?.(row.account).placeholder?.label
            : undefined;
        return (
            accountMatchesCoaGroupKeys({
                accountType: row.type,
                customGroupChainIds: row.customGroupChainIds,
                groupKeys: filters.coaTemplateGroupKeys,
            }) &&
            accountMatchesFilters(
                {
                    name: row.name,
                    codeText: row.codeText,
                    type: row.type,
                    entityId: row.account.entityId,
                    isDisabled: row.account.isDisabled,
                    isManuallyCreated: row.account.isManuallyCreated,
                    ancestorGroupNames: row.tierLabels,
                    hasRole: getAccountRole?.(row.account) != null,
                    hasRule: accountIdsWithEnabledRules?.has(row.account.id),
                    searchLabels: placeholderLabel ? [placeholderLabel] : [],
                },
                {
                    search: searchFilter,
                    accountStatus: filters.accountStatus,
                    accountSource: filters.accountSource,
                    accountRolePresence: filters.accountRolePresence,
                    accountRulePresence: filters.accountRulePresence,
                },
            )
        );
    });
};
